import React, {ReactNode, useContext} from 'react';
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@pokemon/design-v2.button'
import { Form, Field, useField, useForm } from 'react-final-form'
import { FORM_ERROR } from 'final-form'
import { TextField } from '@pokemon/design.ui.input.text-field'
import { PasswordField, validatePassword } from '@pokemon/design.ui.input.password-field'
import { LoadingContext } from '@pokemon/design.ui.loading-spinner'
import { Modal } from '@pokemon/design.ui.containers.modal';
import './create-username-password-page.css'
import { ModalContext } from '@pokemon/design.ui.containers.base-modal';
import { Callout } from '@pokemon/design.ui.containers.callout'
import {handleCreateUserErrors} from "./user-creation-errors";

export const validateUsername = function (t, screenname) {
    return (username: string) => {
        if (!username || username.trim().length === 0) {
            return t('username.required', 'Username is a required field.')
        } else if (!username.match(/^[0-9a-zA-ZÀ-ÿ_+]+$/)) {
            return t('username.characters', 'Username must contain only valid characters.')
        } else if (username.length < 6) {
            return t('username.min_length', 'Username must be more than 6 characters long.')
        } else if (username.length > 16) {
            return t('username.max_length', 'Username must be less than 17 characters long.')
        } else if (username === screenname) {
            return t('error_username_is_screenname', 'Your username cannot be the same as your screenname.')
        } else {
            return null
        }
    }
}

export interface UserCreationErrors {
    [key: string]: string[] | undefined;
}

export type CreateUsernamePasswordPageProps = {
  /**
   * a boolean to determine if account is for adult or child
   */
  isChild: boolean;
  /**
   * an async function to create user. Should return one of...
   *
   *    returning values:
   *        response from /create-user in ACAPI = creation was not successful for some other, unexpected, reasons which the form should display in one or more contingency callout boxes. Newline for each error.
   *            Documentaion for /create-user and ACAPI: https://tpci.atlassian.net/wiki/spaces/ONLINEDEV/pages/29792715/Account+Creation+API
   *
   *        undefined = unrecoverable error or success - either option the provided function should handle itself
   *
   */
  attemptUserCreation: (username: string, password: string) => Promise<undefined | UserCreationErrors>
  /**
   * user's screenname that cannot match username
   */
  screenname?: string;
};

export function CreateUsernamePasswordPage({ isChild, attemptUserCreation, screenname }: CreateUsernamePasswordPageProps) {
  const { setLoading } = useContext(LoadingContext)
  const { openModal } = useContext(ModalContext)
  const { t} = useTranslation()
  const onSubmit = (t, setLoading) => async (values, _, callback) => {
    setLoading(true)
    const result = await attemptUserCreation(values.username, values.password)
    if (result && Object.keys(result).length > 0) {
        callback(handleCreateUserErrors(t, result))
    } else {
      callback()
    }
    setLoading(false)
  }

  return <Form onSubmit={onSubmit(t, setLoading)}
    render={
      ({ handleSubmit, submitting, submitError, hasSubmitErrors, hasValidationErrors, modifiedSinceLastSubmit, form }) => (
      <form onSubmit={handleSubmit}>
        <div className="login-info">
          <h1>{isChild ? t('child_login_info', 'Child\'s Login Information') : t('registration_header', 'Enter the username and password')}</h1>
          <p>{isChild ? t('enter_login_info', 'Enter the username and password you would like to use to log in to your account') : null}</p>
        </div>
        <Field
          name="username"
          validate={validateUsername(t, screenname)}
        >
          {({input, meta}) => (
            <React.Fragment>
              <TextField
                placeholder={t('username_placeholder', 'Username')}
                errorText={(!meta.modifiedSinceLastSubmit && meta.submitError) || (meta.touched && meta.error)}
                {...input}
                name="Username"/>
            </React.Fragment>
          )}
        </Field>
        <div style={{marginTop: '8px', marginBottom: '16px', textAlign: 'left'}}>
          <Trans
            t={t}
            i18nKey='do_not_share_username'
            defaults='You will use your username to log in to your account, or connected apps. &lt;strong&gt;Do not share your username&lt;/strong&gt; and it &lt;strong&gt;needs to be different than your screen name.&lt;/strong&gt;'
            components={{strong: <strong/>}} />
        </div>
        <Field name="password"
          validate={text => !validatePassword(text)} >
          {({input, meta}) => (
            <React.Fragment>
              <PasswordField {...input}/>
            </React.Fragment>
          )}
        </Field>
        {submitError && !submitting &&
            <div className="password-submission-errors">
              <Callout className="error-callout" variant='error'>{submitError}</Callout>
            </div>
        }
        <Button style={{ width: '100%', marginTop: '8.75px' }}
          text={isChild ? t('child_create_account_button','Complete Add a Child') : t('create_account_button', 'Create Account')}
          data-testid="create-account-button"
          disabled={submitting || hasValidationErrors || (hasSubmitErrors && !modifiedSinceLastSubmit)}
          onClick={() => {
            openModal('userConfirm')
          }}/>
          <UserConfirmModal isChild={isChild}/>
      </form>
    )}>
  </Form>
}

const UserConfirmModal = ({isChild}) => {
  const { closeModal } = useContext(ModalContext)
  const { submit } = useForm()

  const { input: { value: username } } = useField('username')
  const { t } = useTranslation()

  return <Modal
    name="userConfirm"
    primaryOnClick={() => {
      closeModal('userConfirm')
      submit()
    }}
    title={t('registration_confirm_title', 'Everything correct?')}
    primaryText={t('registration_modal_confirm', 'I Am Sure')}
    closeText={t('registration_modal_cancel', 'Go Back')}
    contentBoxProps={{
      Logo: null
    }}
  >
    <div className='confirm-modal-contents'>
      {isChild ?
        <Trans
        t={t}
        i18nKey='registration_confirm_details_child'
        defaults={`Please double-check that you have accurately entered your child's <strong>username</strong> as they may be unable to recover their account should they forget their username or password.`}
        components={{ strong: <strong /> }}
      />
      : <Trans
        t={t}
        i18nKey='registration_confirm_details'
        defaults='Please double-check that you have accurately entered your <strong>username</strong> as you may be unable to recover your account should you forget your username or password.'
        components={{ strong: <strong /> }}
      />}
      <div className='grey-callout-box' style={{ marginTop: '10px', marginBottom: '10px' }}>
        {username}
      </div>
      <p style={{ marginBottom: '30px' }}>
        {isChild ?
          t('registration_confirm_details_2_child', 'Once you have created a Pokémon Trainer Club account for your child, you cannot change their birthday or username.')
          : t('registration_confirm_details_2', 'Once you have created a Pokémon Trainer Club account, you cannot change your birthday or username.')
        }
      </p>
    </div>
  </Modal>
}
